import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Loader2, Moon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function NightlySyncCard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/sync/nightly").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = d?.runs?.[0]?.status === "running";
  useEffect(() => { if (!running) return; const t = setInterval(load, 8000); return () => clearInterval(t); }, [running, load]);
  const run = async () => {
    try { const { data } = await api.post("/sync/nightly/run"); toast.success(data.already_running ? "A sync is already running" : "Sync started — about 15 minutes"); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start sync"); }
  };
  if (!d) return null;
  const last = d.runs[0];
  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-6 mb-8" data-testid="nightly-sync-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl text-brand-ink flex items-center gap-2"><Moon className="h-4 w-4 text-brand-gold" /> Nightly Yardi sync</h2>
          <p className="text-xs text-brand-muted mt-1">{d.schedule}: office availability &amp; pricing, new/ending contracts, member directory and day passes.</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={run} disabled={running} data-testid="nightly-sync-run" className="inline-flex items-center gap-2 rounded-full border border-brand-border px-4 py-2 text-sm hover:bg-brand-ink hover:text-brand-page disabled:opacity-50 transition-colors">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {running ? "Syncing…" : "Run now"}
          </button>
        )}
      </div>
      <div className="mt-4" data-testid="nightly-sync-last">
        {!last ? <p className="text-sm text-brand-tertiary">No runs recorded yet — the first one runs tonight.</p> : (
          <>
            <p className="text-sm text-brand-ink">
              Last run {when(last.started_at)} · <span className={last.status === "ok" ? "text-emerald-700" : last.status === "running" ? "text-sky-700" : "text-amber-700"}>{last.status === "ok" ? "All good" : last.status === "running" ? "Running" : "Finished with issues"}</span>
              <span className="text-brand-tertiary"> · {last.trigger === "cron" ? "scheduled" : "manual"}</span>
            </p>
            <ul className="mt-2 space-y-1">
              {last.steps.map((s) => (
                <li key={s.name} className="text-xs flex items-start gap-1.5">
                  {s.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-px" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-px" />}
                  <span className="text-brand-ink">{s.name}</span><span className="text-brand-tertiary">— {s.ok ? s.detail : s.error} ({s.seconds}s)</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <p className="text-[11px] text-brand-tertiary mt-4 border-t border-brand-border pt-3">
        Always live (no sync needed): {d.live.join(", ")}. Frequent: {d.frequent.join(", ")}.
      </p>
    </div>
  );
}
